import React, { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listLibrary, listAssets, uploadMedia } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Sparkle, UploadSimple, Images, Palette, X, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SOURCES = [
  ["ai", "AI Generate", Sparkle],
  ["upload", "Upload Image", UploadSimple],
  ["library", "Media Library", Images],
  ["brand", "Brand Assets", Palette],
];

const itemUrl = (it) =>
  it.image_path ? `${BACKEND}${it.image_path}` : it.media_url ? `${BACKEND}${it.media_url}` : it.url ? `${BACKEND}${it.url}` : "";

export function ImageSourcePicker({ clientId, onChange }) {
  const [source, setSource] = useState("ai");
  const [selected, setSelected] = useState(null); // {id, url}
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [enhance, setEnhance] = useState(true);
  const fileRef = useRef();

  const libQ = useQuery({ queryKey: ["src-lib", clientId], queryFn: () => listLibrary(clientId, { media_type: "image" }), enabled: source === "library" && !!clientId });
  const assetQ = useQuery({ queryKey: ["src-assets", clientId], queryFn: () => listAssets(clientId), enabled: source === "brand" && !!clientId });

  const pickSource = (s) => {
    setSource(s); setSelected(null);
    onChange(s === "ai" ? { image_source: "ai" } : { image_source: s });
  };
  const pickLibrary = (it) => { setSelected({ id: it.id, url: itemUrl(it) }); onChange({ image_source: source, selected_library_id: it.id }); };
  const pickAsset = (it) => { setSelected({ id: it.id, url: itemUrl(it) }); onChange({ image_source: "brand", selected_asset_id: it.id }); };

  const doUpload = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name || "")) { toast.error("JPG or PNG only"); return; }
    setUploading(true);
    try {
      const doc = await uploadMedia(clientId, file, file.name);
      setSelected({ id: doc.id, url: itemUrl(doc) });
      onChange({ image_source: "upload", selected_library_id: doc.id, enhance_uploaded: enhance });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const clearSel = () => { setSelected(null); onChange({ image_source: source }); };

  return (
    <div className="mt-4" data-testid="image-source-picker">
      <Label className="label-caps">Image source</Label>
      <div className="grid grid-cols-4 gap-2 mt-1">
        {SOURCES.map(([k, label, Icon]) => (
          <button key={k} data-testid={`img-source-${k}`} onClick={() => pickSource(k)}
            className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-[11px] font-semibold transition-colors ${source === k ? "border-[#6B3DFF]/60 bg-[#6B3DFF]/10 text-white" : "border-white/10 text-[#9a9a9a] hover:border-white/25"}`}>
            <Icon size={17} weight={source === k ? "fill" : "regular"} /> {label}
          </button>
        ))}
      </div>

      {source === "ai" && (
        <p className="text-[11px] text-[#7a7a7a] mt-2">AI creates a fresh on-brand image for each post.</p>
      )}

      {source === "upload" && (
        <div className="mt-3">
          {selected ? (
            <div>
              <SelPreview url={selected.url} onClear={clearSel} />
              <label data-testid="enhance-upload-row" className="mt-2.5 flex items-start gap-2.5 rounded-lg border border-white/10 bg-[#10101A] px-3 py-2.5 cursor-pointer max-w-md">
                <input data-testid="toggle-enhance-upload" type="checkbox" checked={enhance}
                  onChange={(e) => { setEnhance(e.target.checked); onChange({ image_source: "upload", selected_library_id: selected.id, enhance_uploaded: e.target.checked }); }}
                  className="mt-0.5 h-4 w-4 accent-[#6B3DFF]" />
                <div>
                  <div className="text-sm text-white">AI scene enhancement</div>
                  <div className="text-[11px] text-[#7a7a7a]">Keeps your exact product &amp; packaging pixels untouched — AI only upgrades the background, lighting and composition. Off = post your photo exactly as uploaded.</div>
                </div>
              </label>
            </div>
          ) : (
            <div data-testid="upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${drag ? "border-[#6B3DFF] bg-[#6B3DFF]/5" : "border-white/15 hover:border-white/30"}`}>
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-[#cfcfcf]"><CircleNotch size={16} className="animate-spin" /> Uploading…</div>
              ) : (
                <>
                  <UploadSimple size={22} className="mx-auto mb-2 text-[#9a9a9a]" />
                  <div className="text-sm text-white">Drag &amp; drop, or <span className="brand-text font-semibold">click to browse</span></div>
                  <div className="text-[11px] text-[#7a7a7a] mt-1">JPG or PNG · auto-cropped to square</div>
                </>
              )}
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" data-testid="upload-file-input"
                onChange={(e) => doUpload(e.target.files?.[0])} />
            </div>
          )}
        </div>
      )}

      {source === "library" && (
        <Grid loading={libQ.isLoading} items={(libQ.data || []).filter((i) => itemUrl(i))} selectedId={selected?.id} onPick={pickLibrary} testid="library-grid" empty="No images in your library yet." />
      )}

      {source === "brand" && (
        <Grid loading={assetQ.isLoading} items={(assetQ.data || []).filter((i) => itemUrl(i))} selectedId={selected?.id} onPick={pickAsset} testid="brand-grid" empty="No brand assets uploaded yet." />
      )}
    </div>
  );
}

function SelPreview({ url, onClear }) {
  return (
    <div className="relative inline-block" data-testid="selected-image-preview">
      <img src={url} alt="selected" className="h-28 w-28 object-cover rounded-lg border border-white/10" />
      <button onClick={onClear} data-testid="clear-selected-image" className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white hover:bg-black">
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}

function Grid({ loading, items, selectedId, onPick, testid, empty }) {
  if (loading) return <div className="mt-3 text-xs text-[#7a7a7a] flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Loading…</div>;
  if (!items.length) return <div className="mt-3 text-xs text-[#7a7a7a]">{empty}</div>;
  return (
    <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-52 overflow-y-auto pr-1" data-testid={testid}>
      {items.map((it) => {
        const u = it.image_path ? `${BACKEND}${it.image_path}` : it.media_url ? `${BACKEND}${it.media_url}` : `${BACKEND}${it.url}`;
        const sel = selectedId === it.id;
        return (
          <button key={it.id} data-testid={`pick-image-${it.id}`} onClick={() => onPick(it)}
            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${sel ? "border-[#6B3DFF]" : "border-transparent hover:border-white/25"}`}>
            <img src={u} alt={it.title || ""} className="h-full w-full object-cover" loading="lazy" />
          </button>
        );
      })}
    </div>
  );
}
